import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

function Navbar({ isOverlay = false, isScrolled: externalIsScrolled = null }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
    const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
    const [internalIsScrolled, setInternalIsScrolled] = useState(false);
    const location = useLocation();
    const isHomePage = location.pathname === '/';

    // Use external scroll state if provided, otherwise use internal
    const isScrolled = externalIsScrolled !== null ? externalIsScrolled : internalIsScrolled;

    useEffect(() => {
        // Only set up internal scroll detection if no external scroll state is provided
        if (externalIsScrolled === null) {
            let ticking = false;

            const handleScroll = () => {
                if (!ticking) {
                    requestAnimationFrame(() => {
                        setInternalIsScrolled(window.scrollY > 50);
                        ticking = false;
                    });
                    ticking = true;
                }
            };

            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => window.removeEventListener('scroll', handleScroll);
        }
    }, [externalIsScrolled]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    // Determine navbar styling based on page, scroll position, and overlay mode
    const getNavbarClasses = () => {
        const paddingClasses = isScrolled ? 'py-3' : 'py-6';

        if (isOverlay) {
            // Overlay mode - always transparent with backdrop blur
            return `text-white transition-all duration-300 ${paddingClasses} bg-transparent backdrop-blur-sm`;
        } else {
            // Normal mode - sticky positioning
            const baseClasses = "text-white sticky top-0 z-50 transition-all duration-300";

            if (isHomePage && !isScrolled) {
                // Transparent on home page when at top
                return `${baseClasses} ${paddingClasses} bg-transparent backdrop-blur-sm`;
            } else {
                // Solid background when scrolled or on other pages
                return `${baseClasses} ${paddingClasses} bg-brand-green shadow-lg`;
            }
        }
    };

    return (
        <>
            <nav className={getNavbarClasses()}>
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center space-x-3">
                                <img
                                    src={require((isOverlay || (isHomePage && !isScrolled)) ? '../images/brand/logo-white.png' : '../images/brand/logo-black.png')}
                                    alt="Mega Property Solutions Logo"
                                    className={`w-auto transition-all duration-300 ${isScrolled ? 'h-7' : 'h-12'}`}
                                />
                                <span className={`font-bold transition-all duration-300 ${isScrolled ? 'text-lg' : 'text-3xl'}`}>
                                    Mega Property Solutions
                                </span>
                            </Link>
                        </div>
                        <div className="hidden md:flex space-x-8">
                            <Link
                                to="/"
                                className={`hover:text-brand-orange transition-colors ${(isOverlay || (isHomePage && !isScrolled)) ? 'text-shadow-lg' : ''}`}
                            >
                                Home
                            </Link>
                            <Link
                                to="/about"
                                className={`hover:text-brand-orange transition-colors ${(isOverlay || (isHomePage && !isScrolled)) ? 'text-shadow-lg' : ''}`}
                            >
                                About
                            </Link>
                            {/* Services Dropdown */}
                            <div
                                className="relative"
                                onMouseEnter={() => setIsServicesDropdownOpen(true)}
                                onMouseLeave={() => setIsServicesDropdownOpen(false)}
                            >
                                <div
                                    className={`hover:text-brand-orange transition-colors flex items-center cursor-pointer ${(isOverlay || (isHomePage && !isScrolled)) ? 'text-shadow-lg' : ''}`}
                                >
                                    Services
                                    <svg
                                        className={`ml-1 w-4 h-4 transition-transform duration-200 ${isServicesDropdownOpen ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                <AnimatePresence>
                                    {isServicesDropdownOpen && (
                                        <motion.div
                                            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl py-2 z-50 border border-gray-700"
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                            onMouseEnter={() => setIsServicesDropdownOpen(true)}
                                            onMouseLeave={() => setIsServicesDropdownOpen(false)}
                                        >
                                            <Link
                                                to="/insulation"
                                                className="block px-4 py-2 text-white hover:bg-brand-orange hover:text-white transition-colors rounded-md mx-1"
                                                onClick={() => setIsServicesDropdownOpen(false)}
                                            >
                                                Insulation
                                            </Link>
                                            <Link
                                                to="/garage-door"
                                                className="block px-4 py-2 text-white hover:bg-brand-orange hover:text-white transition-colors rounded-md mx-1"
                                                onClick={() => setIsServicesDropdownOpen(false)}
                                            >
                                                Garage Door
                                            </Link>
                                            <Link
                                                to="/healthy-home"
                                                className="block px-4 py-2 text-white hover:bg-brand-orange hover:text-white transition-colors rounded-md mx-1"
                                                onClick={() => setIsServicesDropdownOpen(false)}
                                            >
                                                Healthy Home
                                            </Link>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            <Link
                                to="/products"
                                className={`hover:text-brand-orange transition-colors ${(isOverlay || (isHomePage && !isScrolled)) ? 'text-shadow-lg' : ''}`}
                            >
                                Products
                            </Link>
                            <Link
                                to="/contact"
                                className={`hover:text-brand-orange transition-colors ${(isOverlay || (isHomePage && !isScrolled)) ? 'text-shadow-lg' : ''}`}
                            >
                                Contact
                            </Link>
                        </div>
                        <div className="md:hidden">
                            <button
                                className="text-white hover:text-brand-orange transition-colors"
                                onClick={toggleMenu}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        className="fixed inset-0 bg-brand-green z-50 md:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <motion.div
                            className="flex flex-col items-center justify-center h-full"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 20, opacity: 0 }}
                            transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
                        >
                            <motion.button
                                className="absolute top-4 right-4 text-white hover:text-brand-orange transition-colors"
                                onClick={closeMenu}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{ duration: 0.3, delay: 0.2 }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </motion.button>

                            <motion.div
                                className="text-center space-y-6 w-full px-4"
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 30, opacity: 0 }}
                                transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                            >
                                {[
                                    { to: "/", text: "Home" },
                                    { to: "/about", text: "About" }
                                ].map((link, index) => (
                                    <motion.div
                                        key={link.to}
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        exit={{ y: 20, opacity: 0 }}
                                        transition={{
                                            duration: 0.4,
                                            delay: 0.4 + (index * 0.1),
                                            ease: "easeOut"
                                        }}
                                    >
                                        <Link
                                            to={link.to}
                                            className="block text-2xl font-semibold text-white hover:text-brand-orange transition-colors"
                                            onClick={closeMenu}
                                        >
                                            {link.text}
                                        </Link>
                                    </motion.div>
                                ))}

                                {/* Services with submenu */}
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: 20, opacity: 0 }}
                                    transition={{
                                        duration: 0.4,
                                        delay: 0.4 + (2 * 0.1),
                                        ease: "easeOut"
                                    }}
                                >
                                    <button
                                        className="text-2xl font-semibold text-white hover:text-brand-orange transition-colors flex items-center justify-center mx-auto"
                                        onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                                    >
                                        Services
                                        <svg
                                            className={`ml-2 w-5 h-5 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    <AnimatePresence>
                                        {isMobileServicesOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="overflow-hidden mt-4 space-y-4"
                                            >
                                                <Link
                                                    to="/insulation"
                                                    className="block text-xl font-medium text-white/80 hover:text-brand-orange transition-colors"
                                                    onClick={closeMenu}
                                                >
                                                    Insulation
                                                </Link>
                                                <Link
                                                    to="/garage-door"
                                                    className="block text-xl font-medium text-white/80 hover:text-brand-orange transition-colors"
                                                    onClick={closeMenu}
                                                >
                                                    Garage Door
                                                </Link>
                                                <Link
                                                    to="/healthy-home"
                                                    className="block text-xl font-medium text-white/80 hover:text-brand-orange transition-colors"
                                                    onClick={closeMenu}
                                                >
                                                    Healthy Home
                                                </Link>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>

                                {[
                                    { to: "/products", text: "Products" },
                                    { to: "/contact", text: "Contact" }
                                ].map((link, index) => (
                                    <motion.div
                                        key={link.to}
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        exit={{ y: 20, opacity: 0 }}
                                        transition={{
                                            duration: 0.4,
                                            delay: 0.4 + ((index + 3) * 0.1),
                                            ease: "easeOut"
                                        }}
                                    >
                                        <Link
                                            to={link.to}
                                            className="block text-2xl font-semibold text-white hover:text-brand-orange transition-colors"
                                            onClick={closeMenu}
                                        >
                                            {link.text}
                                        </Link>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

export default Navbar;
